import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Persist the selected period + year across the three dashboards
 * (Panoramica, Area, Negozio) so a user reviewing Q1 across pages
 * doesn't have to re-select every time.
 *
 * Order of precedence at first render:
 *   1) URL search params (?period=...&year=...) — shareable links win
 *   2) localStorage — last viewed by this user
 *   3) provided defaults
 *
 * On change, the hook keeps both URL and localStorage in sync.
 *
 * period values: '1'..'12' (months), 'Q1'..'Q4' (quarters), 'YEAR' (annual).
 */
const KEY = 'storeops:period';
const EVENT = 'storeops:period-changed';

function readStored() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readFromUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const p = sp.get('period');
    const y = sp.get('year');
    return {
      period: p || null,
      year: y ? Number(y) : null,
    };
  } catch {
    return { period: null, year: null };
  }
}

export function usePersistedPeriod(defaultPeriod = '1', defaultYear = 2026) {
  const navigate = useNavigate();
  const location = useLocation();

  const [period, setPeriodLocal] = useState(() => {
    const u = readFromUrl();
    return u.period || readStored().period || defaultPeriod;
  });
  const [year, setYearLocal] = useState(() => {
    const u = readFromUrl();
    return u.year || readStored().year || defaultYear;
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ period, year }));
      window.dispatchEvent(new CustomEvent(EVENT, { detail: { period, year } }));
    } catch { /* storage might be blocked (private mode); silent no-op */ }
    // Sync URL params without polluting browser history (replace, not push).
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('period') !== String(period)) sp.set('period', String(period));
      if (sp.get('year') !== String(year)) sp.set('year', String(year));
      const newSearch = `?${sp.toString()}`;
      if (newSearch !== window.location.search) {
        navigate({ pathname: location.pathname, search: newSearch }, { replace: true });
      }
    } catch { /* navigation guard */ }
  }, [period, year, navigate, location.pathname]);

  // Re-sync when another component on the same tab changes the period.
  useEffect(() => {
    const onChange = (e) => {
      const d = e.detail || {};
      if (d.period != null && d.period !== period) setPeriodLocal(d.period);
      if (d.year != null && d.year !== year) setYearLocal(d.year);
    };
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, [period, year]);

  return { period, setPeriod: setPeriodLocal, year, setYear: setYearLocal };
}

/** Read-only subscription to the current persisted period. Components
 * that don't need setters can use this to stay in sync reactively. */
export function useCurrentPeriod() {
  const [state, setState] = useState(() => {
    const s = readStored();
    return { period: s.period || null, year: s.year || null };
  });
  useEffect(() => {
    // Re-read on mount (covers the race where the provider useEffect fires
    // before we subscribe) and subscribe to in-tab + cross-tab updates.
    const sync = () => {
      const s = readStored();
      setState(prev => (prev.period === s.period && prev.year === s.year)
        ? prev
        : { period: s.period || null, year: s.year || null });
    };
    sync();
    const onChange = (e) => {
      const d = e.detail || {};
      setState({ period: d.period ?? null, year: d.year ?? null });
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return state;
}

/** Pretty-print the current period for display. */
const MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
export function formatPeriod(period, year) {
  if (!period || !year) return '—';
  if (period === 'YEAR') return `Anno ${year}`;
  if (typeof period === 'string' && period.startsWith('Q')) return `${period} ${year}`;
  const m = Number(period);
  if (m >= 1 && m <= 12) return `${MONTHS[m - 1]} ${year}`;
  return String(period);
}

